import React, { useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Activity, Flag, Crosshair, CornerDownRight, Gauge, Clock, BarChart3, Swords, Trophy, Target, Lock, CheckCircle2, AlertTriangle, GitBranch, Sparkles } from "lucide-react";
import { useLive, usePrematch, useMatchEnrichment, useLiveDetail, useLiveCenter, useMarketHubMap } from "@/hooks/useAtlas";
import { useSession } from "@/context/SessionContext";
import { useLaunch } from "@/context/LaunchContext";
import { LoadingRows, ErrorState, EmptyState } from "@/components/common/States";
import { MarketBadge, CompetitionLabel, ResultBadge, RiskChip } from "@/components/badges/Badges";
import { TeamLogo, logoFrom, resolveTeamVisual } from "@/components/common/TeamLogo";
import { MarketHubSections } from "@/components/cards/MarketHub";
import { LiveLocked } from "@/components/cards/LiveLocked";
import { FollowButton, AddToSlipButton } from "@/components/cards/Actions";
import { AtlasAssistantPanel } from "@/components/atlas/AtlasAssistantPanel";
import { ATLAS_ASSISTANT_ENABLED } from "@/config/features";
import { IntelligenceCard } from "@/components/cards/Cards";
import { parseDecisionReason, lifecycleOf } from "@/presentation/resultPresentation";
import { displayMinute, displayHomeScore, displayAwayScore } from "@/presentation/liveState";
import { fixtureMatches } from "@/presentation/fixtureIdentity";
import { localizeGrade, localizeStepTitle } from "@/presentation/liveLabels";
import { resolveMarket } from "@/presentation/marketRegistry";
import { presentOdd } from "@/presentation/oddsPresentation";
import { scoreLine, clockHM, kickoffLabel } from "@/lib/format";
import { LiveSignal } from "@/types/api";
import { useI18n } from "@/i18n";

function Stat({ icon, label, value }: { icon: React.ReactNode; label: string; value: React.ReactNode }) {
  return (
    <div className="panel flex items-center gap-3 p-4">
      <span className="grid h-9 w-9 place-items-center rounded-lg bg-atlas/10 text-atlas-400">{icon}</span>
      <div><p className="num text-lg font-bold text-foreground">{value}</p><p className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</p></div>
    </div>
  );
}

export default function MatchDetail() {
  const { id } = useParams();
  const { d, lang } = useI18n();
  const { isAuthenticated } = useSession();
  const live = useLive(80);
  const prematch = usePrematch(60);
  const enrich = useMatchEnrichment(id);

  const liveSignals = useMemo(() => (live.data?.rows || []).filter((r) => fixtureMatches(r, id)), [live.data, id]);
  const preFixture = useMemo(() => (prematch.data?.rows || []).find((f) => fixtureMatches(f, id)), [prematch.data, id]);

  const isLoading0 = live.isLoading && prematch.isLoading;
  const primary: LiveSignal | undefined = liveSignals.find((s) => lifecycleOf(s.status, s.won) === "PENDING") || liveSignals[0];
  const preBest = preFixture?.best || preFixture?.markets?.[0] || null;
  // Rich per-decision ATLAS intelligence (real reasoning + auditable replay trail).
  const detail = useLiveDetail(primary?.id);
  const dd = detail.data;
  // Authoritative per-fixture Live Center (real score/minute/events/statistics).
  // LIVE is a backend entitlement (PRO+); never fetch it for FREE.
  const canLive = useLaunch().hasPlan("PRO");
  const lcQ = useLiveCenter(id, isAuthenticated && canLive);
  const { lookup: hubLookup } = useMarketHubMap();
  const hub = hubLookup(id);
  const lc = lcQ.data;

  // Live Center is a first-class existence source: a LIVE NOW fixture has a
  // detail page even without an ATLAS signal. The route param IS the canonical
  // fixture_id (never a decision/row id).
  const home = primary?.home_team || preFixture?.home_team || lc?.home_team || "";
  const away = primary?.away_team || preFixture?.away_team || lc?.away_team || "";
  const country = primary?.country || preFixture?.country || lc?.country || null;
  const league = primary?.league || preFixture?.league || lc?.league || null;
  const found = !!primary || !!preFixture || !!lc;
  // Wait for the live-center lookup to settle before declaring "not found" for a
  // signal-less fixture (prevents false "Maç bulunamadı" on real LIVE fixtures).
  const isLoading = isLoading0 || (!primary && !preFixture && !lc && isAuthenticated && !lcQ.isFetched);

  const dec = primary ? parseDecisionReason(primary.decision_reason) : {};
  const primaryKey = primary ? lifecycleOf(primary.status, primary.won, { resolved_at: primary.resolved_at, final_home_goals: primary.final_home_goals, final_away_goals: primary.final_away_goals }) : "PENDING";
  const primaryResolved = primaryKey !== "PENDING";

  // Current-match hero values: the authoritative live-center feed (lc) is the
  // real current state and wins over the signal snapshot; final for resolved;
  // snapshot only as last resort. Logos only from real backend fields (live-
  // center → signal → prematch), never fabricated.
  const heroMinute = lc?.minute ?? (primary ? displayMinute(primary) : null);
  const heroHome = lc?.home_goals ?? (primary ? (primaryResolved ? primary.final_home_goals : displayHomeScore(primary)) : null);
  const heroAway = lc?.away_goals ?? (primary ? (primaryResolved ? primary.final_away_goals : displayAwayScore(primary)) : null);
  const heroHomeLogo = resolveTeamVisual(home, "home", [lc, primary, preFixture]);
  const heroAwayLogo = resolveTeamVisual(away, "away", [lc, primary, preFixture]);
  const heroLeagueLogo = logoFrom(lc, "league") ?? logoFrom(primary, "league") ?? logoFrom(preFixture, "league");

  // "Why ATLAS sees it" — evidence points strictly from real data.
  const evidence: string[] = [];
  if (dec.dominant) evidence.push(lang === "tr" ? `Bu maçta baskı ${dec.dominant} tarafında${dec.pressureGap != null ? ` (baskı farkı ${dec.pressureGap.toFixed(1)})` : ""}.` : `Pressure is with ${dec.dominant}${dec.pressureGap != null ? ` (gap ${dec.pressureGap.toFixed(1)})` : ""}.`);
  if (dec.shots != null) evidence.push(lang === "tr" ? `Toplam ${dec.shots} şut${dec.shotsOn != null ? `, ${dec.shotsOn} tanesi isabetli.` : "."}` : `${dec.shots} shots${dec.shotsOn != null ? `, ${dec.shotsOn} on target.` : "."}`);
  if (dec.corners != null) evidence.push(lang === "tr" ? `${dec.corners} korner ile hücum baskısı sürüyor.` : `${dec.corners} corners indicate sustained attacking pressure.`);
  if (primary?.direction) evidence.push(lang === "tr" ? `Momentum ${primary.direction === "HOME" ? "ev sahibi" : "deplasman"} yönünde.` : `Momentum favours the ${primary.direction === "HOME" ? "home" : "away"} side.`);
  if (preBest?.model_probability != null) evidence.push(lang === "tr" ? `ATLAS bu seçime %${preBest.model_probability.toFixed(0)} güven veriyor.` : `ATLAS assigns ${preBest.model_probability.toFixed(0)}% confidence to this pick.`);

  const label = `${home} — ${away}`;
  const en = enrich.data;

  // FREE has no LIVE entitlement. This route only ever mounts for LIVE fixtures
  // (fs:* prematch is routed to PrematchDetail), so FREE gets the fully locked
  // LIVE experience — ZERO current score/minute/team/market/signal rendered.
  if (!canLive) {
    return (
      <div data-testid="match-detail-page">
        <Link to="/app/live" className="mb-5 inline-flex items-center gap-1.5 text-sm font-semibold text-muted-foreground transition hover:text-atlas-300">
          <ArrowLeft className="h-4 w-4" /> {d.common.back}
        </Link>
        <LiveLocked teaserCount={null} />
      </div>
    );
  }

  return (
    <div data-testid="match-detail-page">
      <Link to="/app/live" className="mb-5 inline-flex items-center gap-1.5 text-sm font-semibold text-muted-foreground transition hover:text-atlas-300">
        <ArrowLeft className="h-4 w-4" /> {d.common.back}
      </Link>

      {isLoading ? <LoadingRows count={4} />
        : !found ? <EmptyState title={lang === "tr" ? "Maç bulunamadı" : "Match not found"} hint={lang === "tr" ? "Bu maç şu anda aktif zekâ akışında değil." : "This match is not currently in the active intelligence stream."} />
        : (
          <>
            {/* MATCH HERO */}
            <div className="panel relative overflow-hidden p-6" data-testid="match-hero">
              <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-atlas/10 blur-3xl" />
              <div className="flex items-center justify-between gap-3">
                <CompetitionLabel country={country} league={league} leagueLogo={heroLeagueLogo} />
                {primary ? <ResultBadge status={primary.status} won={primary.won} isLive={primaryKey === "PENDING"} />
                  : lc ? <span className="num inline-flex items-center gap-1.5 rounded-md bg-live/10 px-2 py-1 text-[11px] font-bold text-live-400" data-testid="hero-live-badge">{lc.minute != null && <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-live" />}{lc.minute != null ? `${lc.minute}'` : ""} {lc.lifecycle_state || lc.status || (lang === "tr" ? "Canlı" : "Live")}</span>
                  : <span className="inline-flex items-center gap-1 rounded-md border border-white/10 bg-white/5 px-2 py-1 text-[11px] font-semibold text-muted-foreground"><Clock className="h-3 w-3" /> {preFixture ? kickoffLabel(preFixture.fixture_date, lang) : clockHM(null)}</span>}
              </div>
              <div className="mt-6 grid grid-cols-[1fr_auto_1fr] items-center gap-4">
                <div className="flex flex-col items-center gap-2 sm:flex-row sm:justify-end">
                  <TeamLogo src={heroHomeLogo} name={home} size={40} rounded="rounded-lg" />
                  <p className="text-center text-base font-bold text-foreground sm:text-right sm:text-xl">{home}</p>
                </div>
                <div className="text-center">
                  {primary ? <div className="num text-3xl font-extrabold text-foreground sm:text-4xl">{scoreLine(heroHome, heroAway)}</div>
                    : lc && (lc.home_goals != null || lc.away_goals != null) ? <div className="num text-3xl font-extrabold text-foreground sm:text-4xl" data-testid="hero-live-score">{scoreLine(heroHome, heroAway)}</div>
                    : <span className="text-sm text-muted-foreground">vs</span>}
                  {heroMinute != null && (primaryKey === "PENDING" || !primary) && <div className="num mt-1 text-xs font-bold text-live-400">{heroMinute}'</div>}
                </div>
                <div className="flex flex-col items-center gap-2 sm:flex-row sm:justify-start">
                  <TeamLogo src={heroAwayLogo} name={away} size={40} rounded="rounded-lg" />
                  <p className="text-center text-base font-bold text-foreground sm:text-left sm:text-xl">{away}</p>
                </div>
              </div>
              <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
                <FollowButton fixtureId={id as string} label={label} league={league} />
                {(primary || preBest) && <AddToSlipButton selection={{ fixtureId: id as string, home, away, country: country ?? null, league: league ?? null, marketCode: (primary?.market_code || preBest?.market_code) as string, marketName: primary?.market_name || preBest?.market_name || null, confidence: primary?.model_probability ?? preBest?.model_probability ?? null, edge: primary?.edge ?? preBest?.edge ?? null, referenceOdd: primary?.odd ?? preBest?.reference_odd ?? null, kickoff: preFixture?.fixture_date || primary?.created_at || null }} />}
              </div>
            </div>

            <div className="mt-6 grid gap-6 xl:grid-cols-[1.5fr_1fr]">
              <div className="space-y-6">
                {hub && (
                  <IntelligenceCard title={<span className="inline-flex items-center gap-2"><Sparkles className="h-4 w-4 text-atlas-400" /> {lang === "tr" ? "ATLAS Seçimleri" : "ATLAS Selections"}</span>}>
                    <MarketHubSections fixture={hub} variant="detail" />
                  </IntelligenceCard>
                )}
                {/* ATLAS VIEW */}
                {(primary || preBest) && (() => {
                  const mkt = primary?.market_code || preBest?.market_code;
                  const mktName = primary?.market_name || preBest?.market_name;
                  const conf = primary?.model_probability ?? preBest?.model_probability;
                  const edge = primary?.edge ?? preBest?.edge;
                  const odd = presentOdd({ value: primary?.odd ?? preBest?.reference_odd, source: null });
                  return (
                    <IntelligenceCard title={<span className="inline-flex items-center gap-2"><Activity className="h-4 w-4 text-atlas-400" /> {lang === "tr" ? "ATLAS Değerlendirmesi" : "ATLAS Assessment"}</span>}>
                      <div className="rounded-2xl border border-atlas/25 bg-atlas/[0.06] p-5">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="eyebrow text-atlas-300">{lang === "tr" ? "ATLAS Seçimi" : "ATLAS Pick"}</p>
                            <p className="mt-2 text-2xl font-bold tracking-tight text-foreground">{resolveMarket(mkt, lang, mktName).label}</p>
                          </div>
                          <RiskChip code={mkt ?? null} size="md" />
                        </div>
                        {(() => {
                          const chips: { label: string; value: string; accent: string }[] = [];
                          if (conf != null) chips.push({ label: d.common.confidence, value: `${conf.toFixed(0)}%`, accent: "text-atlas-300" });
                          if (edge != null) chips.push({ label: "Edge", value: edge.toFixed(1), accent: "text-foreground/90" });
                          if (odd.show) chips.push({ label: d.common.refOdds, value: odd.value as string, accent: "text-atlas-300" });
                          if (chips.length === 0) {
                            return (
                              <p className="mt-4 text-xs text-muted-foreground" data-testid="atlas-metrics-empty">
                                {lang === "tr" ? "Bu seçim için güven, edge ve referans oran verisi henüz mevcut değil." : "Confidence, edge and reference odds are not available for this pick yet."}
                              </p>
                            );
                          }
                          return (
                            <div className="mt-4 flex flex-wrap gap-3" data-testid="atlas-metrics">
                              {chips.map((c) => (
                                <div key={c.label} className="min-w-[120px] flex-1 rounded-xl border border-white/5 bg-white/[0.03] p-3">
                                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{c.label}</p>
                                  <p className={`num mt-1 text-lg font-bold ${c.accent}`}>{c.value}</p>
                                </div>
                              ))}
                            </div>
                          );
                        })()}
                      </div>

                      {/* WHY ATLAS SEES IT */}
                      <p className="mt-5 mb-2 text-sm font-semibold text-foreground">{lang === "tr" ? "ATLAS neden bu seçimi yaptı?" : "Why did ATLAS pick this?"}</p>
                      {evidence.length > 0 ? (
                        <ul className="space-y-2" data-testid="atlas-evidence">
                          {evidence.slice(0, 5).map((e, i) => (
                            <li key={i} className="flex items-start gap-2 rounded-xl border border-white/5 bg-white/[0.02] p-3 text-sm text-foreground/90">
                              <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-atlas/15 text-[10px] font-bold text-atlas-300">{i + 1}</span>{e}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="rounded-xl border border-white/5 bg-white/[0.02] p-4 text-sm text-muted-foreground">{lang === "tr" ? "Bu karşılaşmada karar gerekçesi için veri henüz yeterli değil." : "There isn't enough data to explain this decision yet."}</p>
                      )}

                      {(dec.shots != null || dec.shotsOn != null || dec.corners != null || primary?.direction) && (
                        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
                          <Stat icon={<Crosshair className="h-4 w-4" />} label={d.common.shots} value={dec.shots ?? "—"} />
                          <Stat icon={<Crosshair className="h-4 w-4" />} label={d.common.shotsOn} value={dec.shotsOn ?? "—"} />
                          <Stat icon={<CornerDownRight className="h-4 w-4" />} label={d.common.corners} value={dec.corners ?? "—"} />
                          <Stat icon={<Flag className="h-4 w-4" />} label={d.common.momentum} value={primary?.direction === "HOME" ? (lang === "tr" ? "Ev sahibi" : "Home") : primary?.direction === "AWAY" ? (lang === "tr" ? "Deplasman" : "Away") : (dec.dominant || "—")} />
                        </div>
                      )}
                      {dec.pressureGap != null && (
                        <div className="mt-3">
                          <div className="mb-1 flex items-center justify-between text-xs text-muted-foreground"><span>{d.common.pressure}</span><span className="num text-foreground/90">{dec.pressureGap.toFixed(2)}</span></div>
                          <div className="h-2 overflow-hidden rounded-full bg-white/5"><div className="h-full rounded-full bg-gradient-to-r from-atlas-500 to-atlas-400" style={{ width: `${Math.min(100, Math.abs(dec.pressureGap) * 4)}%` }} /></div>
                        </div>
                      )}
                    </IntelligenceCard>
                  );
                })()}

                {/* No ATLAS signal yet — honest message inside the ATLAS section only */}
                {!primary && !preBest && (
                  <IntelligenceCard title={<span className="inline-flex items-center gap-2"><Activity className="h-4 w-4 text-atlas-400" /> {lang === "tr" ? "ATLAS Değerlendirmesi" : "ATLAS Assessment"}</span>}>
                    <p className="rounded-xl border border-white/8 bg-white/[0.02] p-4 text-sm text-muted-foreground" data-testid="atlas-no-signal">
                      {lang === "tr" ? "ATLAS bu maç için henüz aktif sinyal üretmedi." : "ATLAS has not produced an active signal for this match yet."}
                    </p>
                  </IntelligenceCard>
                )}

                {/* ATLAS INTELLIGENCE TRAIL — real reasoning, auditable replay, resolution */}
                {primary && (dd?.decision_replay?.timeline?.length || dd?.explainable_ai || dd?.prediction_outcome?.available) && (
                  <IntelligenceCard title={<span className="inline-flex items-center gap-2"><GitBranch className="h-4 w-4 text-atlas-400" /> {lang === "tr" ? "ATLAS Zekâ Kaydı" : "ATLAS Intelligence Trail"}</span>}>
                    {/* reasoning summary + labels */}
                    {dd?.explainable_ai?.summary && (
                      <p className="rounded-xl border border-atlas/20 bg-atlas/[0.05] p-3 text-sm text-foreground/90" data-testid="atlas-trail-summary">{dd.explainable_ai.summary}</p>
                    )}
                    {(dd?.explainable_ai?.confidence_label || dd?.pattern?.pattern_strength || dd?.learning?.grade) && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {dd?.explainable_ai?.confidence_label && <span className="inline-flex items-center gap-1.5 rounded-full border border-atlas/25 bg-atlas/10 px-2.5 py-1 text-xs font-semibold text-atlas-300"><Gauge className="h-3.5 w-3.5" /> {d.common.confidence}: {localizeGrade(dd.explainable_ai.confidence_label, lang)}</span>}
                        {dd?.pattern?.pattern_strength && <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-xs font-semibold text-foreground/80"><Sparkles className="h-3.5 w-3.5 text-gold-400" /> {lang === "tr" ? "Örüntü" : "Pattern"}: {localizeGrade(dd.pattern.pattern_strength, lang)}</span>}
                        {dd?.learning?.grade && <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-xs font-semibold text-foreground/80">{lang === "tr" ? "Öğrenme" : "Learning"}: {localizeGrade(dd.learning.grade, lang)}{dd.learning.learning_score != null ? ` (${dd.learning.learning_score})` : ""}</span>}
                      </div>
                    )}

                    {/* supporting reasons */}
                    {dd?.explainable_ai?.reasons && dd.explainable_ai.reasons.length > 0 && (
                      <div className="mt-4" data-testid="atlas-trail-reasons">
                        <p className="eyebrow mb-2 text-atlas-300">{lang === "tr" ? "Destekleyen Kanıt" : "Supporting Evidence"}</p>
                        <ul className="space-y-2">
                          {dd.explainable_ai.reasons.slice(0, 5).map((r, i) => (
                            <li key={r.code || i} className="flex items-start gap-2 rounded-xl border border-white/5 bg-white/[0.02] p-3 text-sm text-foreground/90">
                              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-atlas-400" /> <span className="flex-1">{r.title}</span>
                              {r.score != null && <span className="num text-xs font-semibold text-atlas-300">{Number(r.score).toFixed(0)}</span>}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* risks / caution */}
                    {dd?.explainable_ai?.risks && dd.explainable_ai.risks.length > 0 && (
                      <div className="mt-4" data-testid="atlas-trail-risks">
                        <p className="eyebrow mb-2 text-gold-300">{lang === "tr" ? "Dikkat Noktaları" : "Caution Points"}</p>
                        <ul className="space-y-2">
                          {dd.explainable_ai.risks.slice(0, 4).map((r, i) => (
                            <li key={r.code || i} className="flex items-start gap-2 rounded-xl border border-white/5 bg-white/[0.02] p-3 text-sm text-muted-foreground">
                              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-gold-400" /> <span className="flex-1">{r.title}</span>
                              {r.severity && <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{r.severity}</span>}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* auditable decision-replay timeline */}
                    {dd?.decision_replay?.timeline && dd.decision_replay.timeline.length > 0 && (
                      <div className="mt-5" data-testid="atlas-evolution-trail">
                        <p className="eyebrow mb-3 text-foreground/80">{lang === "tr" ? "Karar Evrimi" : "Decision Evolution"}</p>
                        <ol className="relative space-y-3 pl-5">
                          <span className="absolute left-[7px] top-1 bottom-1 w-px bg-white/10" aria-hidden />
                          {dd.decision_replay.timeline.map((step, i) => {
                            const ok = (step.status || "").toUpperCase() === "SUCCESS" && step.available;
                            return (
                              <li key={step.code || i} className="relative">
                                <span className={`absolute -left-5 top-1 grid h-3.5 w-3.5 place-items-center rounded-full ${ok ? "bg-atlas/30" : "bg-white/10"}`}>
                                  <span className={`h-1.5 w-1.5 rounded-full ${ok ? "bg-atlas-400" : "bg-muted-foreground/50"}`} />
                                </span>
                                <p className="text-sm font-semibold text-foreground/90">{localizeStepTitle(step, lang)}</p>
                                {step.summary && <p className="mt-0.5 text-xs text-muted-foreground">{step.summary}</p>}
                              </li>
                            );
                          })}
                        </ol>
                      </div>
                    )}

                    {/* final resolution outcome */}
                    {dd?.prediction_outcome?.available && (dd.prediction_outcome.headline || dd.prediction_outcome.result) && (
                      <div className="mt-5 rounded-xl border border-white/5 bg-white/[0.02] p-4" data-testid="atlas-trail-outcome">
                        <p className="eyebrow mb-1.5 text-gold-300">{lang === "tr" ? "Sonuç" : "Resolution"}</p>
                        {dd.prediction_outcome.headline && <p className="text-sm text-foreground/90">{dd.prediction_outcome.headline}</p>}
                        <div className="mt-2 flex flex-wrap gap-2">
                          {dd.prediction_outcome.result && <span className="rounded-full border border-atlas/25 bg-atlas/10 px-2.5 py-1 text-xs font-semibold text-atlas-300">{localizeGrade(dd.prediction_outcome.result, lang)}</span>}
                          {dd.prediction_outcome.accuracy_grade && <span className="rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-xs font-semibold text-foreground/80">{lang === "tr" ? "Not" : "Grade"}: {localizeGrade(dd.prediction_outcome.accuracy_grade, lang)}</span>}
                        </div>
                      </div>
                    )}
                  </IntelligenceCard>
                )}

                {/* LIVE CENTER — authoritative realtime detail (events + statistics) */}
                {isAuthenticated && (lc || lcQ.isFetched) && (
                  <IntelligenceCard title={<span className="inline-flex items-center gap-2"><Activity className="h-4 w-4 text-live-400" /> {lang === "tr" ? "Canlı Ayrıntı" : "Live Detail"}</span>}>
                    {lc ? (
                      <>
                        <div className="flex flex-wrap items-center gap-2" data-testid="live-center-meta">
                          {(lc.minute != null || lc.status || lc.lifecycle_state) && (
                            <span className="num inline-flex items-center gap-1.5 rounded-full border border-live/25 bg-live/10 px-2.5 py-1 text-xs font-semibold text-live-400">
                              {lc.minute != null ? `${lc.minute}'` : ""} {lc.lifecycle_state || lc.status || ""}
                            </span>
                          )}
                          {(lc.home_goals != null && lc.away_goals != null) && (
                            <span className="num rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-xs font-bold text-foreground">{lc.home_goals} - {lc.away_goals}</span>
                          )}
                          {lc.stale ? (
                            <span className="inline-flex items-center gap-1.5 rounded-full border border-gold/25 bg-gold/10 px-2.5 py-1 text-xs font-semibold text-gold-300">{lang === "tr" ? "Veri gecikmeli" : "Data delayed"}</span>
                          ) : lc.updated_at ? (
                            <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-xs text-muted-foreground"><Clock className="h-3.5 w-3.5" /> {lang === "tr" ? "Güncel" : "Live"}</span>
                          ) : null}
                        </div>

                        {/* events timeline */}
                        {lc.events && lc.events.length > 0 ? (
                          <div className="mt-4" data-testid="live-center-events">
                            <p className="eyebrow mb-2 text-foreground/80">{lang === "tr" ? "Olaylar" : "Timeline"}</p>
                            <ul className="space-y-2">
                              {lc.events.slice(0, 20).map((e, i) => (
                                <li key={i} className="flex items-center gap-3 rounded-xl border border-white/5 bg-white/[0.02] p-2.5 text-sm">
                                  <span className="num w-8 shrink-0 text-center text-xs font-bold text-live-400">{e.minute != null ? `${e.minute}'` : "—"}</span>
                                  <span className="flex-1 text-foreground/90">{[e.type, e.player, e.detail].filter(Boolean).join(" · ") || "—"}</span>
                                  {e.team && <span className="shrink-0 text-xs text-muted-foreground">{e.team}</span>}
                                </li>
                              ))}
                            </ul>
                          </div>
                        ) : (
                          <p className="mt-3 text-xs text-muted-foreground">{lang === "tr" ? "Henüz kayıtlı olay yok." : "No events recorded yet."}</p>
                        )}

                        {/* statistics */}
                        {lc.statistics && lc.statistics.length > 0 && (
                          <div className="mt-4" data-testid="live-center-stats">
                            <p className="eyebrow mb-2 text-foreground/80">{lang === "tr" ? "İstatistikler" : "Statistics"}</p>
                            <div className="space-y-1.5">
                              {lc.statistics.slice(0, 12).map((s, i) => (
                                <div key={s.key || i} className="grid grid-cols-[3rem_1fr_3rem] items-center gap-2 text-sm">
                                  <span className="num text-right font-semibold text-foreground/90">{s.home ?? "—"}</span>
                                  <span className="truncate text-center text-xs text-muted-foreground">{s.label || s.key}</span>
                                  <span className="num font-semibold text-foreground/90">{s.away ?? "—"}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      <p className="text-sm text-muted-foreground" data-testid="live-center-unavailable">
                        {lang === "tr" ? "Canlı ayrıntı (olaylar ve istatistikler) SDIP Live Center bu maç için yayınladığında burada görünecek." : "Live detail (events & statistics) will appear here once the SDIP Live Center publishes it for this match."}
                      </p>
                    )}
                  </IntelligenceCard>
                )}

                {/* TEAM COMPARISON + H2H + LEAGUE — evidence layer. Only real data
                    renders; unavailable blocks are hidden; an empty group collapses
                    to one compact neutral note (no FootyStats-specific copy). */}
                {(() => {
                  const h = en?.home, a = en?.away;
                  const fx = (n: number | null | undefined, dp = 2) => (n != null ? n.toFixed(dp) : null);
                  const pc = (n: number | null | undefined) => (n != null ? `${n.toFixed(0)}%` : null);
                  const rows: { key: string; label: string; icon: React.ReactNode; home: string | null; away: string | null }[] = [];

                  const hForm = h?.formLast5?.length ? h.formLast5.join(" ") : null;
                  const aForm = a?.formLast5?.length ? a.formLast5.join(" ") : null;
                  if (hForm || aForm) rows.push({ key: "form", label: lang === "tr" ? "Form (Son 5)" : "Form (Last 5)", icon: <Activity className="h-3.5 w-3.5 text-atlas-400" />, home: hForm, away: aForm });

                  const hGoals = (h?.goalsForAvg != null || h?.goalsAgainstAvg != null) ? `${fx(h?.goalsForAvg) ?? "—"} / ${fx(h?.goalsAgainstAvg) ?? "—"}` : null;
                  const aGoals = (a?.goalsForAvg != null || a?.goalsAgainstAvg != null) ? `${fx(a?.goalsForAvg) ?? "—"} / ${fx(a?.goalsAgainstAvg) ?? "—"}` : null;
                  if (hGoals || aGoals) rows.push({ key: "goals", label: lang === "tr" ? "Gol (Attı/Yedi)" : "Goals (For/Against)", icon: <Target className="h-3.5 w-3.5 text-atlas-400" />, home: hGoals, away: aGoals });

                  const hXg = (h?.xgFor != null || h?.xgAgainst != null) ? `${fx(h?.xgFor) ?? "—"} / ${fx(h?.xgAgainst) ?? "—"}` : null;
                  const aXg = (a?.xgFor != null || a?.xgAgainst != null) ? `${fx(a?.xgFor) ?? "—"} / ${fx(a?.xgAgainst) ?? "—"}` : null;
                  if (hXg || aXg) rows.push({ key: "xg", label: "xG / xGA", icon: <Gauge className="h-3.5 w-3.5 text-atlas-400" />, home: hXg, away: aXg });

                  const hBtts = pc(h?.bttsPct), aBtts = pc(a?.bttsPct);
                  if (hBtts || aBtts) rows.push({ key: "btts", label: lang === "tr" ? "KG Var" : "BTTS", icon: <Swords className="h-3.5 w-3.5 text-atlas-400" />, home: hBtts, away: aBtts });

                  const hOu = pc(h?.over25Pct), aOu = pc(a?.over25Pct);
                  if (hOu || aOu) rows.push({ key: "ou", label: lang === "tr" ? "Üst 2.5" : "Over 2.5", icon: <BarChart3 className="h-3.5 w-3.5 text-atlas-400" />, home: hOu, away: aOu });

                  const h2hMatches = (en?.h2h?.available && en.h2h.matches?.length) ? en.h2h.matches : null;
                  const standings = (en?.leagueContext?.available && en.leagueContext.standings?.length) ? en.leagueContext.standings : null;
                  const hasAny = rows.length > 0 || !!h2hMatches || !!standings;

                  if (!hasAny) {
                    return (
                      <IntelligenceCard title={<span className="inline-flex items-center gap-2"><BarChart3 className="h-4 w-4 text-atlas-400" /> {lang === "tr" ? "Takım Karşılaştırması" : "Team Comparison"}</span>}>
                        <p className="rounded-xl border border-white/8 bg-white/[0.02] p-4 text-sm text-muted-foreground" data-testid="comparison-empty">
                          {primary ? (lang === "tr" ? "Bu canlı maç için ek istatistik verisi henüz mevcut değil." : "No additional statistics are available for this live match yet.")
                                   : (lang === "tr" ? "Bu maç için ek istatistik verisi henüz mevcut değil." : "No additional match statistics are available yet.")}
                        </p>
                      </IntelligenceCard>
                    );
                  }

                  return (
                    <>
                      {rows.length > 0 && (
                        <IntelligenceCard title={<span className="inline-flex items-center gap-2"><BarChart3 className="h-4 w-4 text-atlas-400" /> {lang === "tr" ? "Takım Karşılaştırması" : "Team Comparison"}</span>}>
                          <div className="mb-2 grid grid-cols-[4rem_1fr_4rem] gap-2 text-[11px] font-semibold text-muted-foreground">
                            <span className="truncate text-left">{home}</span><span className="text-center">{lang === "tr" ? "Metrik" : "Metric"}</span><span className="truncate text-right">{away}</span>
                          </div>
                          <div className="space-y-2" data-testid="comparison-rows">
                            {rows.map((r) => (
                              <div key={r.key} className="grid grid-cols-[4rem_1fr_4rem] items-center gap-2 rounded-xl border border-white/5 bg-white/[0.02] px-3 py-2.5" data-testid="comparison-row">
                                <span className="num text-left text-sm font-semibold text-foreground/90">{r.home ?? "—"}</span>
                                <span className="inline-flex items-center justify-center gap-1.5 text-center text-[11px] text-muted-foreground">{r.icon}{r.label}</span>
                                <span className="num text-right text-sm font-semibold text-foreground/90">{r.away ?? "—"}</span>
                              </div>
                            ))}
                          </div>
                          <p className="mt-3 text-[11px] text-muted-foreground">
                            {lang === "tr" ? "İstatistikler ATLAS kararını destekleyen kanıttır; kararı belirlemez." : "Statistics are evidence supporting the ATLAS decision; they do not determine it."}
                            {en?.source ? ` · ${lang === "tr" ? "Veri" : "Data"}: ${en.source}` : ""}
                          </p>
                        </IntelligenceCard>
                      )}

                      {(h2hMatches || standings) && (
                        <div className="grid gap-6 sm:grid-cols-2">
                          {h2hMatches && (
                            <IntelligenceCard title={<span className="inline-flex items-center gap-2"><Swords className="h-4 w-4 text-atlas-400" /> {lang === "tr" ? "Karşılıklı Maçlar" : "Head to Head"}</span>}>
                              <div className="space-y-2" data-testid="h2h-list">
                                {h2hMatches.slice(0, 6).map((m, i) => (
                                  <div key={i} className="flex items-center justify-between gap-2 rounded-xl border border-white/5 bg-white/[0.02] px-3 py-2 text-sm">
                                    <span className="truncate text-foreground/90">{m.label}</span>
                                    <span className="num shrink-0 font-semibold text-foreground">{m.score}</span>
                                  </div>
                                ))}
                              </div>
                            </IntelligenceCard>
                          )}
                          {standings && (
                            <IntelligenceCard title={<span className="inline-flex items-center gap-2"><Trophy className="h-4 w-4 text-gold-400" /> {lang === "tr" ? "Lig Durumu" : "League Context"}</span>}>
                              <div className="space-y-2" data-testid="standings-list">
                                {standings.slice(0, 6).map((s, i) => (
                                  <div key={i} className="flex items-center justify-between gap-2 rounded-xl border border-white/5 bg-white/[0.02] px-3 py-2 text-sm">
                                    <span className="truncate text-foreground/90"><b className="num mr-1.5 text-muted-foreground">{s.position}.</b>{s.team}</span>
                                    <span className="num shrink-0 font-semibold text-foreground">{s.points}</span>
                                  </div>
                                ))}
                              </div>
                            </IntelligenceCard>
                          )}
                        </div>
                      )}
                    </>
                  );
                })()}

                {/* prematch markets */}
                {preFixture && preFixture.markets?.length > 0 && (
                  <IntelligenceCard title={<span className="inline-flex items-center gap-2"><Gauge className="h-4 w-4 text-atlas-400" /> {lang === "tr" ? "Maç Öncesi Marketler" : "Prematch Markets"}</span>}>
                    <div className="space-y-2">
                      {preFixture.markets.map((m) => {
                        const odd = presentOdd({ value: m.reference_odd, source: null });
                        return (
                          <div key={m.id} className="flex items-center justify-between gap-2 rounded-xl border border-white/5 bg-white/[0.02] p-3" data-testid="detail-market-row">
                            <div className="flex flex-wrap items-center gap-2"><MarketBadge code={m.market_code} backendLabel={m.market_name} size="sm" /><RiskChip code={m.market_code} /></div>
                            <div className="flex items-center gap-3 text-xs text-muted-foreground">
                              {m.model_probability != null && <span>{d.common.confidence} <b className="num text-foreground/90">{m.model_probability.toFixed(0)}%</b></span>}
                              {odd.show && <span className="num font-semibold text-atlas-300">{odd.value}</span>}
                              <ResultBadge status={m.status} won={m.won} size="sm" />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </IntelligenceCard>
                )}

                {/* signal lifecycle — SIGNAL-TIME snapshot (immutable historical
                    metadata), explicitly distinct from the current live state above */}
                {liveSignals.length > 0 && (
                  <IntelligenceCard title={<span className="inline-flex items-center gap-2"><Clock className="h-4 w-4 text-gold-400" /> {lang === "tr" ? "Sinyal Akışı" : "Signal Flow"}</span>}>
                    <p className="-mt-1 mb-3 text-[11px] text-muted-foreground">{lang === "tr" ? "Her satır, sinyalin üretildiği andaki dakika ve skoru gösterir (anlık değil)." : "Each row shows the minute and score at the moment the signal was created (not current)."}</p>
                    <div className="space-y-2">
                      {liveSignals.map((s) => {
                        const sKey = lifecycleOf(s.status, s.won, { resolved_at: s.resolved_at, final_home_goals: s.final_home_goals, final_away_goals: s.final_away_goals });
                        const snapH = sKey !== "PENDING" ? s.final_home_goals : s.initial_home_goals;
                        const snapA = sKey !== "PENDING" ? s.final_away_goals : s.initial_away_goals;
                        return (
                          <div key={s.id} className="flex items-center justify-between gap-2 rounded-xl border border-white/5 bg-white/[0.02] p-3" data-testid="lifecycle-row">
                            <div className="flex min-w-0 items-center gap-3">
                              <span className="num text-xs text-muted-foreground">#{s.id}</span>
                              <MarketBadge code={s.market_code} backendLabel={s.market_name} size="sm" />
                            </div>
                            <div className="flex items-center gap-2.5 text-xs text-muted-foreground">
                              <span className="hidden text-[10px] uppercase tracking-wider text-muted-foreground/80 sm:inline">{lang === "tr" ? "Sinyal anı" : "Signal"}</span>
                              {s.minute != null && <span className="num">{s.minute}'</span>}
                              <span className="num">{scoreLine(snapH, snapA)}</span>
                              <ResultBadge status={s.status} won={s.won} isLive={sKey === "PENDING"} size="sm" />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </IntelligenceCard>
                )}
              </div>

              {ATLAS_ASSISTANT_ENABLED && <AtlasAssistantPanel matchContext={{ fixture_id: id, label }} />}
            </div>
          </>
        )}
    </div>
  );
}
